import { API_URL as API, BACKEND_URL } from '../utils/config';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';


const ProductDetail = () => {
  const { id } = useParams();
  const { language, t } = useLanguage();
  const { addToCart } = useCart();
  
  // Hardcoded products
  const hardcodedProducts = {
    "33707ad3-d38e-4edd-b17b-6f522d1eb475": {
      id: "33707ad3-d38e-4edd-b17b-6f522d1eb475",
      name_en: "Moist Foundation #04 Glow Light",
      name_no: "Moist Foundation #04 Glow Light",
      description_en: "Liquid moist foundation that provides extra nourishment and moisture to the face. Specially developed for Scandinavian skin. Rich in nutrients and care substances. Can also be used as a day cream. Has a silky soft texture that melts into the skin and evens out the skin tone. Offers perfect opacity and cares for the skin simultaneously.",
      description_no: "Flytende fuktighetsfoundation som gir ekstra næring og fuktighet til ansiktet. Spesialutviklet for skandinavisk hud. Rik på næringsstoffer og pleiestoffer. Kan også brukes som dagkrem. Har en silkemyk tekstur som smelter inn i huden og jevner ut hudtonen. Gir perfekt dekning og pleier huden samtidig.",
      image_url: "https://customer-assets.emergentagent.com/job_cosmobeauty-shop/artifacts/x3m2ldde_Screenshot%202025-12-10%20at%2001.02.38.png",
      price_nok: 649,
      shades: [{ shade: "04", name: "Glow Light" }],
      category: "foundation",
      in_stock: true
    },
    "723c6817-4a4f-490c-a156-57e7e9e3848c": {
      id: "723c6817-4a4f-490c-a156-57e7e9e3848c",
      name_en: "Silk Foundation #04 Basic Warm",
      name_no: "Silk Foundation #04 Basic Warm",
      description_en: "100% pure mineral powder foundation suitable for all ages. Offers customizable opacity from natural to full coverage. Particles provide excellent opacity without settling in lines and wrinkles. Contains zinc to soothe skin and benefit acne-prone skin.",
      description_no: "100% ren mineralpudder foundation egnet for alle aldre. Gir justerbar dekning fra naturlig til full dekning. Partikler gir utmerket dekning uten å legge seg i linjer og rynker. Inneholder sink for å berolige huden og er bra for akne-utsatt hud.",
      image_url: "https://customer-assets.emergentagent.com/job_cosmobeauty-shop/artifacts/90lceq6f_Screenshot%202025-12-10%20at%2001.00.26.png",
      price_nok: 549,
      shades: [{ shade: "04", name: "Basic Warm" }],
      category: "foundation",
      in_stock: true
    }
  };
  
  const [product, setProduct] = useState(hardcodedProducts[id] || null);
  const [loading, setLoading] = useState(false);
  const [selectedShade, setSelectedShade] = useState("04");
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);

  const handleAddToCart = async () => {
    if (adding) return;
    
    try {
      setAdding(true);
      await addToCart(product.id, quantity, selectedShade);
      toast.success(t('addedToCart'));
    } catch (error) {
      console.error('Add to cart error:', error);
      toast.error('Failed to add to cart. Please try again.');
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] pt-32 pb-24 flex items-center justify-center" data-testid="loading-product">
        <p className="text-[#8A8A8A]">Loading...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] pt-32 pb-24 flex items-center justify-center" data-testid="product-not-found">
        <p className="text-[#8A8A8A]">Product not found</p>
      </div>
    );
  }

  const name = language === 'no' ? product.name_no : product.name_en;
  const description = language === 'no' ? product.description_no : product.description_en;

  return (
    <div className="min-h-screen bg-[#F9F9F7] pt-32 pb-24" data-testid="product-detail-page">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Product Image */}
          <div className="flex items-center justify-center">
            <img 
              src={product.image_url} 
              alt={name} 
              className="max-w-xs h-auto object-contain"
              data-testid="product-detail-image"
            />
          </div>

          {/* Product Info */}
          <div className="flex flex-col justify-center">
            <p className="text-xs uppercase tracking-[0.2em] text-[#8A8A8A] mb-2">
              {product.category}
            </p>
            <h1 className="text-3xl md:text-5xl font-sans tracking-tight text-[#1A1A1A] mb-6" data-testid="product-detail-name">
              {name}
            </h1>
            <p className="text-2xl font-sans text-[#1A1A1A] mb-6" data-testid="product-detail-price">
              {product.price_nok} NOK
            </p>
            <p className="text-base font-sans leading-relaxed text-[#8A8A8A] mb-8">
              {description}
            </p>

            {/* Shade Selection */}
            {product.shades && product.shades.length > 0 && (
              <div className="mb-6">
                <label className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A] mb-3 block">
                  {t('selectShade')}
                </label>
                <div className="flex flex-wrap gap-3" data-testid="shade-selector">
                  {product.shades.map((shade, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedShade(shade.shade)}
                      className={`px-4 py-2 text-xs uppercase tracking-[0.2em] transition-all duration-300 ${
                        selectedShade === shade.shade
                          ? 'bg-[#1A1A1A] text-white'
                          : 'bg-transparent border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white'
                      }`}
                      data-testid={`shade-btn-${index}`}
                    >
                      {shade.name || shade.shade}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="mb-8">
              <label className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A] mb-3 block">
                {t('quantity')}
              </label>
              <input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                className="w-24 bg-transparent border border-[#1A1A1A] px-4 py-2 text-sm text-[#1A1A1A]"
                data-testid="quantity-input"
              />
            </div>

            {/* Add to Cart Button */}
            <button
              onClick={handleAddToCart}
              disabled={!product.in_stock || adding}
              className="bg-[#1A1A1A] text-white px-8 py-4 text-xs uppercase tracking-[0.2em] hover:bg-[#333333] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="add-to-cart-btn"
            >
              {adding ? 'Adding...' : (product.in_stock ? t('addToCart') : t('outOfStock'))}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;